import { ReactNode, useState } from 'react';

import { useIntl } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';

import Lozenge, { LozengeProps } from '@atlaskit/lozenge';
import { xcss, Box, Stack, Text, Inline } from '@atlaskit/primitives';
import Toggle from '@atlaskit/toggle';
import Tooltip, { TooltipProps } from '@atlaskit/tooltip';

import { Link } from 'src/router';

export type AtlassianIntelligenceLozengeProps = {
  lozenge: LozengeProps;
  message: MessageDescriptor;
};

type AtlassianIntelligenceDescriptionProps = {
  message: MessageDescriptor;
  link?: MessageDescriptor & {
    href: string;
  };
};

export type AtlassianIntelligencePreferenceProps = {
  header: MessageDescriptor;
  subHeader?: AtlassianIntelligenceDescriptionProps;
  description: AtlassianIntelligenceDescriptionProps;
  lozenges?: AtlassianIntelligenceLozengeProps[];
  testId?: string;
} & AtlassianIntelligencePreferenceToggleProps;

export type AtlassianIntelligencePreferenceOnToggleListener = (event: {
  isChecked: boolean;
}) => Promise<{ response?: Response }>;

type AtlassianIntelligencePreferenceToggleProps = {
  isToggleDisabled?: boolean;
  toggleTooltip?: Omit<TooltipProps, 'children'>;
  onToggleChecked?: AtlassianIntelligencePreferenceOnToggleListener;
  isInitiallyChecked?: boolean;
};

export const PreferenceBox = ({
  testId,
  children,
}: {
  testId?: string;
  children: ReactNode | ReactNode[];
}) => {
  const preferenceBoxStyles = xcss({
    display: 'flex',
    padding: 'space.200',
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    borderRadius: 'radius.medium',
    borderWidth: 'border.width',
    borderStyle: 'solid',
    borderColor: 'color.border',
    backgroundColor: 'elevation.surface',
    gap: 'space.200',
  });
  return (
    <Box testId={testId} xcss={preferenceBoxStyles}>
      {children}
    </Box>
  );
};

export const PreferenceHeader = ({
  header,
  lozenges,
}: Pick<AtlassianIntelligencePreferenceProps, 'header' | 'lozenges'>) => {
  const intl = useIntl();

  return (
    <Inline space="space.050" alignBlock="center">
      <Text size="large" weight="bold">
        {intl.formatMessage(header)}
      </Text>
      {lozenges?.map(lozenge => (
        <Lozenge key={lozenge.message.id} {...lozenge.lozenge}>
          {intl.formatMessage(lozenge.message)}
        </Lozenge>
      ))}
    </Inline>
  );
};

export const PreferenceDescription = ({
  description,
}: Pick<AtlassianIntelligencePreferenceProps, 'description'>) => {
  const intl = useIntl();

  const preferenceDescriptionStyles = xcss({
    width: '80%',
  });
  return (
    <Box xcss={preferenceDescriptionStyles}>
      <Text size="medium" weight="regular">
        {intl.formatMessage(description.message)}{' '}
        {description?.link && (
          <Link href={description.link.href} target="_blank">
            {intl.formatMessage(description.link)}
          </Link>
        )}
      </Text>
    </Box>
  );
};

export function AtlassianIntelligencePreference({
  header,
  subHeader,
  description,
  lozenges,
  isToggleDisabled,
  isInitiallyChecked,
  onToggleChecked,
  toggleTooltip,
  testId,
}: AtlassianIntelligencePreferenceProps) {
  const intl = useIntl();

  const PreferenceSubHeader = () => {
    if (!subHeader) {
      return <></>;
    }

    return (
      <Box>
        <Text size="small" weight="regular">
          {intl.formatMessage(subHeader.message)}{' '}
          {subHeader?.link && (
            <Link href={subHeader.link.href} target="_blank">
              {intl.formatMessage(subHeader.link)}
            </Link>
          )}
        </Text>
      </Box>
    );
  };

  const PreferenceToggle = () => {
    /*
      We do not want users to be able to spam this toggle since it could lead to
      multiple requests going out at the same time.

      However, if a parent component tries to control this "disabled" state, the toggle
      will lose ability to "slide" from left to right, and instead, it will just immediately
      transition from off to on.

      Thus, this component needs to control the disabled state with its own state.

      This "isUserInteractionLocked" does not make the toggle gray, but it ignores
      any additional inputs from the user, hence the name, isUserInteractionLocked.
    */
    const [isUserInteractionLocked, setIsUserInteractionLocked] =
      useState(false);
    const [isChecked, setIsChecked] = useState(isInitiallyChecked);

    const preferenceToggle = (
      <Toggle
        testId={`${testId}-toggle`}
        isDisabled={isToggleDisabled}
        isChecked={isChecked}
        onChange={async ({ currentTarget }) => {
          const { checked } = currentTarget;
          const revert = () => {
            setIsChecked(!checked);
            setIsUserInteractionLocked(false);
          };

          const check = () => {
            setIsChecked(checked);
            setIsUserInteractionLocked(true);
          };

          if (isUserInteractionLocked) {
            setIsChecked(!checked);
            return;
          }
          check();

          try {
            if (onToggleChecked) {
              const result = await onToggleChecked({
                isChecked: checked,
              });
              const { response } = result;
              if (!response || !response.ok) {
                revert();
              }
            }
          } finally {
            setIsUserInteractionLocked(false);
          }
        }}
      ></Toggle>
    );

    const shouldUseTooltip = !!toggleTooltip && isToggleDisabled;
    return (
      <>
        {shouldUseTooltip && (
          <Tooltip {...toggleTooltip}>{preferenceToggle} </Tooltip>
        )}
        {!shouldUseTooltip && preferenceToggle}
      </>
    );
  };

  const headerAndDescriptionStackStyles = xcss({
    flex: '1 0 0',
  });
  return (
    <PreferenceBox testId={testId}>
      <Stack space="space.100" xcss={headerAndDescriptionStackStyles}>
        <PreferenceHeader header={header} lozenges={lozenges} />
        {subHeader && <PreferenceSubHeader></PreferenceSubHeader>}
        <PreferenceDescription description={description} />
      </Stack>
      <PreferenceToggle></PreferenceToggle>
    </PreferenceBox>
  );
}
